/**
 * Display-only helpers for TRADE payout / escrow UI — the trade-side mirror of
 * purchasePayoutDisplay.js. These READ fields persisted by backend settlement,
 * webhooks, and release paths (functions/payoutCalc.js, stripeWebhook.js).
 * They do NOT compute fees, net payout, or any amount — zero calculation
 * logic here.
 *
 * Trades store the gross escrow in transferStatus.amount (the webhook also
 * writes escrowAmount with the same value); settlement writes the
 * authoritative net to the TOP-LEVEL payoutAdjustment.expectedNetCents,
 * because the Stripe webhook replaces transferStatus wholesale. The read
 * order below mirrors effectivePayoutCents() in functions/payoutCalc.js for
 * display purposes only. Before this helper, TradeProgress read
 * transferStatus.amount directly and overstated the payout for any receiver
 * who settled with fees-from-cash.
 */

/**
 * Cents the cash receiver gets at escrow release, as stored on the trade doc.
 *
 * 1. payoutAdjustment.expectedNetCents — verbatim when present (authoritative
 *    post-settlement, written by settleTrade / the webhook's F1 safety net).
 * 2. transferStatus.amount — gross escrow, for trades not yet settled.
 * 3. transferStatus.escrowAmount — same value, defensive fallback.
 *
 * Returns null when none of the fields is a finite number.
 */
export const getTradeReceiverPayoutCents = (trade) => {
  const expectedNetCents = trade?.payoutAdjustment?.expectedNetCents;
  if (typeof expectedNetCents === 'number' && Number.isFinite(expectedNetCents)) {
    return expectedNetCents;
  }

  const amount = trade?.transferStatus?.amount;
  if (typeof amount === 'number' && Number.isFinite(amount)) {
    return amount;
  }

  const escrowAmount = trade?.transferStatus?.escrowAmount;
  if (typeof escrowAmount === 'number' && Number.isFinite(escrowAmount)) {
    return escrowAmount;
  }

  return null;
};
